/** Formatting + coercion helpers (pure — unit-tested). */

/** SQL aggregates arrive as strings through JSON; coerce defensively. */
export function n(x: unknown): number {
  if (typeof x === "number") return x;
  if (typeof x === "string" && x.trim() !== "") {
    const v = Number(x);
    return Number.isFinite(v) ? v : 0;
  }
  return 0;
}

export function usd(x: unknown, opts: { compact?: boolean } = {}): string {
  const v = n(x);
  if (opts.compact) {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (Math.abs(v) >= 10_000) return `$${(v / 1_000).toFixed(1)}K`;
  }
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: v >= 100 ? 0 : 2 });
}

export function pct(x: unknown, digits = 1): string {
  return `${(n(x) * 100).toFixed(digits)}%`;
}

export function int(x: unknown): string {
  return Math.round(n(x)).toLocaleString("en-US");
}

/**
 * All dashboard dates/times render in Pacific time (the operator's zone).
 * Inputs are UTC ISO strings from the API; these convert for display only.
 */
export const PT_ZONE = "America/Los_Angeles";

/** Pacific calendar date, e.g. "2026-06-13". */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA", { timeZone: PT_ZONE }); // YYYY-MM-DD
}

/** Pacific date + 24h time with a PT marker, e.g. "06-13 17:26 PT". */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const parts = d.toLocaleString("en-CA", {
    timeZone: PT_ZONE, month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).replace(",", ""); // "06-13 17:26"
  return `${parts} PT`;
}

/** Pacific month-day for compact chart axes, e.g. "06-13". */
export function fmtMonthDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA", { timeZone: PT_ZONE, month: "2-digit", day: "2-digit" });
}

/** Back-compat alias — chart labels used MM-DD; now Pacific. */
export const dayLabel = fmtMonthDay;

/**
 * Trailing-window helpers for the runtime cost-by-day rows.
 *
 * These exist because failure-rate must be windowed, not lifetime. A single
 * bad day (e.g. a generation loop that retried and failed ~63 times) would
 * otherwise dominate an all-time rate forever and keep Agent Ops permanently
 * red. We filter on each row's real `day` against a cutoff rather than slicing
 * a fixed row count (which is wrong when a day has several model rows).
 */
const DAY_MS = 86_400_000;

export function rowsInWindow<T extends { day: string }>(
  rows: T[],
  windowDays: number,
  asOfMs = Date.now(),
): T[] {
  const cutoff = asOfMs - windowDays * DAY_MS;
  return rows.filter((r) => {
    const t = Date.parse(r.day);
    return Number.isFinite(t) && t >= cutoff;
  });
}

/** Failed/invocations and their ratio over a trailing window of `day` rows. */
export function failRateInWindow(
  rows: Array<{ day: string; failed: unknown; invocations: unknown }>,
  windowDays: number,
  asOfMs = Date.now(),
): { rate: number; failed: number; invocations: number } {
  const win = rowsInWindow(rows, windowDays, asOfMs);
  const failed = win.reduce((s, r) => s + n(r.failed), 0);
  const invocations = win.reduce((s, r) => s + n(r.invocations), 0);
  return { rate: invocations > 0 ? failed / invocations : 0, failed, invocations };
}

export function timeAgo(iso: string | null | undefined, nowMs = Date.now()): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
